import { useMessageConsumer } from "../../services/Messaging/MessageConsumer";
import {useQueues} from "../../services/Messaging/QueueAPI";
import HttpService from "../../services/HttpService";
import RenderOnRole from "../../Utilities/RenderOnRole";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "../../css/Pages.css";

const PREDEFINED_FILTER_KEYS = [
    "PanelInterfaceId",
    "PanelBerichtId",
];

const normalizeBase64 = (value) => value.replace(/\s+/g, "").replace(/=+$/, "");

const encodeBytesAsBase64 = (bytes) => {
    const chunkSize = 0x8000;
    let binaryValue = "";

    for (let index = 0; index < bytes.length; index += chunkSize) {
        binaryValue += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
    }

    return btoa(binaryValue);
};

const decodeBase64IfNeeded = (value) => {
    if (typeof value !== "string") return value;

    const trimmedValue = value.trim();
    const compactValue = normalizeBase64(trimmedValue);

    if (!compactValue || compactValue.length % 4 === 1 || !/^[A-Za-z0-9+/]+$/.test(compactValue)) {
        return value;
    }

    try {
        const binaryValue = window.atob(trimmedValue);
        const bytes = Uint8Array.from(binaryValue, (character) => character.charCodeAt(0));
        const decodedValue = new TextDecoder("utf-8").decode(bytes);
        const roundTripValue = encodeBytesAsBase64(new TextEncoder().encode(decodedValue));

        if (normalizeBase64(roundTripValue) !== compactValue) {
            return value;
        }

        return decodedValue;
    } catch {
        return value;
    }
};

const decodeMessageBody = (body) => {
    const bodyText = Array.isArray(body)
        ? new TextDecoder("utf-8").decode(Uint8Array.from(body))
        : body ?? "";

    return decodeBase64IfNeeded(bodyText);
};

const renderHeaderValue = (value) => {
    if (typeof value === "string") {
        return decodeBase64IfNeeded(value);
    }

    if (Array.isArray(value)) {
        return value.map(renderHeaderValue);
    }

    if (value && typeof value === "object") {
        return Object.fromEntries(
            Object.entries(value).map(([key, nestedValue]) => [key, renderHeaderValue(nestedValue)])
        );
    }

    return value;
};

export default function QueueManager() {
    const { t } = useTranslation();

    const {
        vhosts, queues, queueDetail,
        selectedVhost, setSelectedVhost,
        selectedQueue, setSelectedQueue,
        loading,
    } = useQueues();

    const {
        isConnected, message, error, actionMessage, startConsuming, ack, sendToSendmessage, removeRetry, requeue, moveMessage, filterMessages, resetMessage,
        deliverAllXML, uploadProgress, uploadErrors, uploadCompleted,
    }
        = useMessageConsumer(selectedVhost);

    const [targetQueue, setTargetQueue] = useState("");
    const [messageFilters, setMessageFilters] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [messageDetails, setMessageDetails] = useState(null);
    const [messageDetailsLoading, setMessageDetailsLoading] = useState(false);
    const [messageDetailsError, setMessageDetailsError] = useState(null);
    const fileInputRef = useRef(null);

    const connectionState = isConnected
        ? t("messageManagementPage.statusConnected")
        : t("messageManagementPage.statusDisconnected");
    const queueState = selectedQueue ? selectedQueue : t("messageManagementPage.noQueueSelected");
    const isRetryQueue = /^retry-\d+m$/.test(selectedQueue ?? "");
    const canSendToSendmessage = selectedQueue === "dead-letter" || isRetryQueue;
    const headers = message?.Headers ?? message?.headers ?? null;
    const panelBerichtIdHeader = Object.entries(headers ?? {})
        .find(([key]) => key.toLowerCase() === "panelberichtid")?.[1];
    const decodedPanelBerichtId = panelBerichtIdHeader === undefined
        ? null
        : decodeBase64IfNeeded(String(panelBerichtIdHeader));
    const panelBerichtId = decodedPanelBerichtId === null
        ? null
        : Number.parseInt(decodedPanelBerichtId, 10);
    const hasValidPanelBerichtId = Number.isInteger(panelBerichtId);
    const filterKeyOptions = Array.from(new Set([
        ...PREDEFINED_FILTER_KEYS,
        ...Object.keys(headers ?? {}),
    ]));
    const getVhostName = (vhost) => (typeof vhost === "string" ? vhost : vhost?.name ?? vhost?.vhost ?? "");
    const getQueueName = (queue) => (typeof queue === "string" ? queue : queue?.name ?? queue?.queueName ?? queue?.vhostQueueName ?? "");

    useEffect(() => {
        let isCurrentRequest = true;

        const fetchMessageDetails = async () => {
            if (!hasValidPanelBerichtId) {
                setMessageDetails(null);
                setMessageDetailsError(null);
                return;
            }

            setMessageDetailsLoading(true);
            setMessageDetailsError(null);

            try {
                const axiosInstance = HttpService.getAxiosClient();
                const params = new URLSearchParams({
                    "Filters.BerichtId": String(panelBerichtId),
                    PageNumber: "0",
                    PageCount: "50",
                });
                const response = await axiosInstance.get(
                    `/Berichten?${params.toString()}`
                );

                if (isCurrentRequest) {
                    setMessageDetails(response.data);
                }
            } catch (err) {
                if (isCurrentRequest) {
                    setMessageDetails(null);
                    setMessageDetailsError(err.message);
                }
            } finally {
                if (isCurrentRequest) {
                    setMessageDetailsLoading(false);
                }
            }
        };

        fetchMessageDetails();

        return () => {
            isCurrentRequest = false;
        };
    }, [panelBerichtId, hasValidPanelBerichtId]);

    const fetchNextMessage = async () => {
        if (!selectedQueue) return;

        await startConsuming(selectedQueue);
    };

    const handleAck = async () => {
        if (!selectedQueue) return;

        await ack();
        if(hasValidMessageFilters){
            await handleFilterMessages();
        }
        else{
            await fetchNextMessage();
        }
    };

    const handleRequeue = async () => {
        if (!selectedQueue) return;

        await requeue();
        if(hasValidMessageFilters){
            await handleFilterMessages();
        }
        else{
            await fetchNextMessage();
        }
    };

    const handleMove = async () => {
        if (!selectedQueue || !targetQueue.trim()) return;

        await moveMessage(targetQueue);
        if(hasValidMessageFilters){
            await handleFilterMessages();
        }
        else{
            await fetchNextMessage();
        }
    };

    const handleSendToSendmessage = async () => {
        if (!message || !canSendToSendmessage) return;

        await sendToSendmessage();
        if(hasValidMessageFilters){
            await handleFilterMessages();
        }
        else{
            await fetchNextMessage();
        }
    };

    const handleRemoveRetry = async () => {
        if (!message || !isRetryQueue) return;

        await removeRetry();
        if(hasValidMessageFilters){
            await handleFilterMessages();
        }
        else{
            await fetchNextMessage();
        }
    };

    const addMessageFilter = () => {
        setMessageFilters((prev) => [
            ...prev,
            {
                id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                key: "",
                type: "string",
                value: "",
            },
        ]);
    };

    const updateMessageFilter = (id, field, nextValue) => {
        setMessageFilters((prev) =>
            prev.map((filter) =>
                filter.id === id
                    ? { ...filter, [field]: nextValue }
                    : filter
            )
        );
    };

    const removeMessageFilter = (id) => {
        setMessageFilters((prev) => prev.filter((filter) => filter.id !== id));
    };

    const buildFilterHeaders = () =>
        messageFilters.reduce((headersObject, filter) => {
            const key = filter.key.trim();
            if (!key) return headersObject;

            if (filter.type === "int") {
                const parsedValue = Number.parseInt(filter.value, 10);
                if (Number.isNaN(parsedValue)) return headersObject;
                headersObject[key] = parsedValue;
                return headersObject;
            }

            headersObject[key] = filter.value;
            return headersObject;
        }, {});

    const hasValidMessageFilters = messageFilters.some((filter) => {
        if (!filter.key.trim()) return false;
        if (filter.type === "int") {
            return filter.value !== "" && !Number.isNaN(Number.parseInt(filter.value, 10));
        }

        return true;
    });

    const handleFilterMessages = async () => {
        if (!selectedQueue) return;

        const headersObject = buildFilterHeaders();
        if (Object.keys(headersObject).length === 0) return;

        resetMessage();
        await filterMessages(selectedQueue, headersObject);
    };

    const handleFilesSelected = (event) => {
        setSelectedFiles(Array.from(event.target.files ?? []));
    };

    const handleDeliverXML = async () => {
        if (!selectedFiles.length || !isConnected || !selectedQueue) return;

        setUploading(true);
        try {
            const connectMessages = await Promise.all(selectedFiles.map((file) => file.text()));
            await deliverAllXML(connectMessages, selectedQueue);
        } finally {
            setUploading(false);
        }
    };

    const clearSelectedFiles = () => {
        setSelectedFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const requeueCurrentMessage = async () => {
        if (!message) return;

        await requeue();
        setTargetQueue("");
    };

    const renderHeaders = () => {
        if (!headers) return null;

        const headerEntries = Object.entries(headers);

        if (headerEntries.length === 0) {
            return <div className="loading-text">{t("messageManagementPage.noHeadersAvailable")}</div>;
        }

        return (
            <div style={{ display: "grid", gap: "0.75rem" }}>
                {headerEntries.map(([key, value]) => (
                    <div
                        key={key}
                        style={{
                            display: "grid",
                            gridTemplateColumns: "minmax(140px, 220px) minmax(0, 1fr)",
                            gap: "0.75rem",
                            alignItems: "start",
                            padding: "0.8rem 0.9rem",
                            borderRadius: "0.5rem",
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                        }}
                    >
                        <div className="parameter-label" style={{ marginBottom: 0, width: "auto" }}>
                            {key}
                        </div>
                        <pre
                            style={{
                                margin: 0,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                backgroundColor: "transparent",
                                color: "#334155",
                                fontSize: "0.875rem",
                                lineHeight: 1.55,
                            }}
                        >
                            {typeof value === "string"
                                ? renderHeaderValue(value)
                                : JSON.stringify(renderHeaderValue(value), null, 2)}
                        </pre>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="page-wrapper">
            <div className="page-header">
                <h2>{t("messageManagementPage.title")}</h2>
            </div>

            <div className="page-content">
                <div className="filter-bar">
                    <h3>{t("messageManagementPage.connectionTitle")}</h3>
                    <div className="filter-grid">
                        <div className="filter-group">
                            <label className="filter-label" htmlFor="vhost-select">
                                {t("messageManagementPage.vhostLabel")}
                            </label>
                            <select
                                id="vhost-select"
                                className="filter-select"
                                value={selectedVhost ?? ""}
                                onChange={async (e) => {
                                    const nextVhost = e.target.value;
                                    await requeueCurrentMessage();
                                    setSelectedVhost(nextVhost);
                                    setSelectedQueue("");
                                }}
                            >
                                <option value="" disabled>
                                    {t("messageManagementPage.selectVhost")}
                                </option>
                                {vhosts.map((v) => {
                                    const vhostName = getVhostName(v);

                                    return (
                                        <option key={vhostName} value={vhostName}>
                                            {vhostName}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label className="filter-label" htmlFor="queue-select">
                                {t("messageManagementPage.queueLabel")}
                            </label>
                            <select
                                id="queue-select"
                                className="filter-select"
                                value={selectedQueue ?? ""}
                                onChange={async (e) => {
                                    const nextQueue = e.target.value;
                                    await requeueCurrentMessage();
                                    setSelectedQueue(nextQueue);
                                }}
                                disabled={!selectedVhost}
                            >
                                <option value="" disabled>
                                    {selectedVhost
                                        ? t("messageManagementPage.selectQueue")
                                        : t("messageManagementPage.chooseVhostFirst")}
                                </option>
                                {queues.map((q) => {
                                    const queueName = getQueueName(q);

                                    return (
                                        <option key={queueName} value={queueName}>
                                            {queueName}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="parameter-section">
                    <h3>{t("messageManagementPage.queueOverviewTitle")}</h3>
                    <div className="parameter-group">
                        <span className="parameter-label">{t("messageManagementPage.connectionStatusLabel")}</span>
                        <div>{connectionState}</div>
                    </div>
                    <div className="parameter-group">
                        <span className="parameter-label">{t("messageManagementPage.selectedQueueLabel")}</span>
                        <div>{queueState}</div>
                    </div>
                    <div className="parameter-group">
                        <span className="parameter-label">{t("messageManagementPage.messagesReadyLabel")}</span>
                        <div>{loading ? t("common.loading") : queueDetail?.messages ?? "-"}</div>
                    </div>
                    <div className="parameter-group">
                        <span className="parameter-label">{t("messageManagementPage.consumersLabel")}</span>
                        <div>{loading ? t("common.loading") : queueDetail?.consumers ?? "-"}</div>
                    </div>

                    <div className="action-bar">
                        <div className="action-buttons">
                            <button
                                className="btn btn-primary"
                                onClick={fetchNextMessage}
                                disabled={!isConnected || loading || !selectedQueue}
                            >
                                {t("messageManagementPage.getNextMessage")}
                            </button>
                            {/* <button
                                className="btn btn-secondary"
                                onClick={() => {
                                    requeueCurrentMessage();
                                    setSelectedQueue("");
                                    setTargetQueue("");
                                }}
                                disabled={!selectedVhost && !selectedQueue && !targetQueue}
                            >
                                {t("messageManagementPage.clearQueue")}
                            </button> */}
                        </div>
                    </div>
                </div>

                <div className="parameter-section">
                    <h3>{t("messageManagementPage.xmlDeliveryTitle")}</h3>
                    <div className="parameter-group">
                        <label className="parameter-label" htmlFor="xml-file-input">
                            {t("messageManagementPage.xmlFilesLabel")}
                        </label>
                        <input
                            id="xml-file-input"
                            ref={fileInputRef}
                            className="parameter-input"
                            type="file"
                            accept=".xml,text/xml,application/xml"
                            multiple
                            onChange={handleFilesSelected}
                            disabled={uploading}
                        />
                    </div>
                    {selectedFiles.length > 0 && (
                        <div className="loading-text">
                            {t("messageManagementPage.filesSelected", { count: selectedFiles.length })}
                        </div>
                    )}
                    <div className="action-bar">
                        <div className="action-buttons">
                            <button
                                className="btn btn-primary"
                                type="button"
                                onClick={handleDeliverXML}
                                disabled={!isConnected || !selectedQueue || !selectedFiles.length || uploading}
                            >
                                {uploading
                                    ? t("messageManagementPage.uploadingXML")
                                    : t("messageManagementPage.deliverXML")}
                            </button>
                            <button
                                className="btn btn-secondary"
                                type="button"
                                onClick={clearSelectedFiles}
                                disabled={!selectedFiles.length || uploading}
                            >
                                {t("messageManagementPage.clearXMLFiles")}
                            </button>
                        </div>
                    </div>
                    {(uploading || uploadCompleted !== null) && (
                        <div className="parameter-group">
                            <span className="parameter-label">{t("messageManagementPage.uploadProgressLabel")}</span>
                            <div>
                                {uploadCompleted !== null
                                    ? t("messageManagementPage.uploadCompleted", { count: uploadCompleted })
                                    : t("messageManagementPage.uploadProgress", {
                                        current: uploadProgress.Current,
                                        total: uploadProgress.Total,
                                    })}
                            </div>
                        </div>
                    )}
                    {uploadErrors.length > 0 && (
                        <div className="error-alert">
                            <h3>{t("messageManagementPage.uploadErrorsTitle")}</h3>
                            {uploadErrors.map((uploadError) => (
                                <p key={`${uploadError.number}-${uploadError.error}`}>
                                    {t("messageManagementPage.uploadFileError", uploadError)}
                                </p>
                            ))}
                        </div>
                    )}
                </div>

                <div className="parameter-section">
                        <div className="parameter-label" style={{ width: "auto", marginBottom: "0.5rem" }}>
                            {t("messageManagementPage.filterMenuTitle")}
                        </div>
                        <div style={{ display: "grid", gap: "0.75rem" }}>
                            <datalist id="message-filter-key-options">
                                {filterKeyOptions.map((keyOption) => (
                                    <option key={keyOption} value={keyOption} />
                                ))}
                            </datalist>
                            {messageFilters.length === 0 ? (
                                <div className="loading-text">{t("messageManagementPage.noFiltersAdded")}</div>
                            ) : (
                                messageFilters.map((filter) => (
                                    <div
                                        key={filter.id}
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "minmax(180px, 1.5fr) minmax(120px, 0.7fr) minmax(160px, 1fr) auto",
                                            gap: "0.75rem",
                                            alignItems: "center",
                                        }}
                                    >
                                        <input
                                            className="parameter-input"
                                            type="text"
                                            placeholder={t("messageManagementPage.filterKeyPlaceholder")}
                                            value={filter.key}
                                            list="message-filter-key-options"
                                            onChange={(e) => updateMessageFilter(filter.id, "key", e.target.value)}
                                        />
                                        <select
                                            className="parameter-select"
                                            value={filter.type}
                                            onChange={(e) => updateMessageFilter(filter.id, "type", e.target.value)}
                                        >
                                            <option value="string">{t("messageManagementPage.filterTypeString")}</option>
                                            <option value="int">{t("messageManagementPage.filterTypeInt")}</option>
                                        </select>
                                        <input
                                            className="parameter-input"
                                            type={filter.type === "int" ? "number" : "text"}
                                            step={filter.type === "int" ? "1" : undefined}
                                            placeholder={t("messageManagementPage.filterValuePlaceholder")}
                                            value={filter.value}
                                            onChange={(e) => updateMessageFilter(filter.id, "value", e.target.value)}
                                        />
                                        <button
                                            className="btn btn-secondary"
                                            type="button"
                                            onClick={() => removeMessageFilter(filter.id)}
                                            aria-label={t("messageManagementPage.removeFilter")}
                                        >
                                            x
                                        </button>
                                    </div>
                                ))
                            )}

                            <div className="action-buttons" style={{ justifyContent: "flex-start" }}>
                                <button
                                    className="btn btn-secondary"
                                    type="button"
                                    onClick={addMessageFilter}
                                >
                                    {t("messageManagementPage.addFilter")}
                                </button>
                                <button
                                    className="btn btn-primary"
                                    type="button"
                                    onClick={handleFilterMessages}
                                    disabled={!isConnected || loading || !selectedQueue || !hasValidMessageFilters}
                                >
                                    {t("messageManagementPage.applyFilters")}
                                </button>
                            </div>
                        </div>
                    </div>
                    {error && (
                    <div className="error-alert">
                        <h3>{t("messageManagementPage.errorTitle")}</h3>
                        <p>{error}</p>
                    </div>
                )}
                    {actionMessage && (
                        <div className="parameter-group">
                            <span className="parameter-label">{t("messageManagementPage.sendToSendmessageResult")}</span>
                            <div>{typeof actionMessage === "string" ? actionMessage : JSON.stringify(actionMessage)}</div>
                        </div>
                    )}

                <div className="parameter-section">
                    <h3>{t("messageManagementPage.messageInspectorTitle")}</h3>

                    <div className="action-bar" style={{ marginTop: "1rem" }}>
                        <div className="action-buttons">
                            <button className="btn btn-error" onClick={handleAck} disabled={!message}>
                                {t("messageManagementPage.acknowledge")}
                            </button>
                            <button className="btn btn-success" onClick={handleRequeue} disabled={!message}>
                                {t("messageManagementPage.requeue")}
                            </button>
                            {canSendToSendmessage && (
                                <button className="btn btn-primary" onClick={handleSendToSendmessage} disabled={!message}>
                                    {t("messageManagementPage.sendToSendmessage")}
                                </button>
                            )}
                            {isRetryQueue && (
                                <button className="btn btn-secondary" onClick={handleRemoveRetry} disabled={!message}>
                                    {t("messageManagementPage.removeRetry")}
                                </button>
                            )}
                        </div>
                    </div>

                    <RenderOnRole roles={["Administrator"]}>
                        <div className="parameter-group">
                            <label className="parameter-label" htmlFor="target-queue">
                                {t("messageManagementPage.moveToQueueLabel")}
                            </label>
                            <select
                                id="target-queue"
                                className="parameter-select"
                                value={targetQueue}
                                onChange={(e) => setTargetQueue(e.target.value)}
                                disabled={!message}
                            >
                                <option value="" disabled>
                                    {t("messageManagementPage.selectTargetQueue")}
                                </option>
                                {queues.map((queue) => {
                                    const queueName = getQueueName(queue);

                                    return (
                                        <option key={queueName} value={queueName}>
                                            {queueName}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        <div className="action-bar" style={{ marginTop: "1rem" }}>
                            <div className="action-buttons">
                                <button
                                    className="btn btn-primary"
                                    onClick={handleMove}
                                    disabled={!message || !targetQueue.trim()}
                                >
                                    {t("messageManagementPage.moveToQueue")}
                                </button>
                            </div>
                        </div>
                    </RenderOnRole>

                    <div>
                                <div className="parameter-label" style={{ width: "auto", marginBottom: "0.5rem" }}>
                            {t("messageManagementPage.headersLabel")}
                                </div>
                                {renderHeaders()}
                    </div>

                    {message ? (
                        <div style={{ display: "grid", gap: "1rem" }}>
                            <div>
                                <div className="parameter-label" style={{ width: "auto", marginBottom: "0.5rem" }}>
                                    {t("messageManagementPage.bodyLabel")}
                                </div>
                                <pre
                                    style={{
                                        margin: 0,
                                        padding: "1rem",
                                        backgroundColor: "#0f172a",
                                        color: "#e2e8f0",
                                        borderRadius: "0.5rem",
                                        overflowX: "auto",
                                        fontSize: "0.875rem",
                                        lineHeight: 1.6,
                                    }}
                                >
                                    {decodeMessageBody(message.body)}
                                </pre>
                            </div>
                            {hasValidPanelBerichtId && (
                                <div>
                                    <div className="parameter-label" style={{ width: "auto", marginBottom: "0.5rem" }}>
                                        {t("messageManagementPage.relatedMessagesLabel")}
                                    </div>
                                    {messageDetailsLoading && <div className="loading-text">{t("common.loading")}</div>}
                                    {messageDetailsError && <div className="error-alert"><p>{messageDetailsError}</p></div>}
                                    {!messageDetailsLoading && !messageDetailsError && messageDetails !== null && (
                                        <pre
                                            style={{
                                                margin: 0,
                                                padding: "1rem",
                                                backgroundColor: "#f8fafc",
                                                color: "#334155",
                                                border: "1px solid #e2e8f0",
                                                borderRadius: "0.5rem",
                                                overflowX: "auto",
                                                fontSize: "0.875rem",
                                                lineHeight: 1.6,
                                            }}
                                        >
                                            {JSON.stringify(messageDetails, null, 2)}
                                        </pre>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="loading-container" style={{ height: "auto", minHeight: "8rem" }}>
                            <div className="loading-text">
                                {t("messageManagementPage.noMessageLoaded", {
                                    action: t("messageManagementPage.getNextMessage"),
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}